/* eslint-disable react/no-unused-prop-types */
import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "@mui/material";
import { useEtherscanUrl } from "~/features/web3/useEtherscanUrl";
import {
    ArtworkLinkClickedEventTypes,
    EventNames,
} from "~/api/hubspot/hubspotEvents";
import { getUtms } from "~/api/hubspot/hubspot";
import { trackHubSpotEvent, trackSegmentEvent } from "~/api/choreography";
import { useCognitoUser } from "~/features/user/useCognitoUser";
import { SegmentEvents } from "~/api/segment/segmentEvents";
import { ArtworkInfoDesktopView } from "./ArtworkInfoDesktopView";
import { ArtworkInfoMobileView } from "./ArtworkInfoMobileView";
import { useCheckTokenOwnership } from "~/features/user/useCheckTokenOwnership";
import { downloadFile as downloadFileSaga } from "~/api/downloadSaga";
import { TokenDataSchema } from "~/api/ERC721v4/TokenDataJSONSchema";

export interface ArtworkInfoProps {
    tokenData: TokenDataSchema;
    hasActionButton: boolean;
}

export const ArtworkInfo: React.FC<ArtworkInfoProps> = ({
    tokenData,
    hasActionButton,
}) => {
    const [openTooltip, setOpenTooltip] = useState(false);
    const ownerWallet = tokenData.ownerOf || "";
    const user = useCognitoUser();
    const dispatch = useDispatch();
    const isDesktop = useMediaQuery("(min-width:1100px)");
    const ownedByYou = useCheckTokenOwnership(ownerWallet);
    const etherscanUrl = useEtherscanUrl(tokenData.blockchainId);

    const handleTooltipClose = useCallback(() => {
        setOpenTooltip(false);
    }, []);

    const handleTooltipOpen = useCallback(() => {
        setOpenTooltip(true);
        setTimeout(() => {
            setOpenTooltip(false);
        }, 3000);
    }, []);

    const handleOnChainIdClick = useCallback(() => {
        dispatch(
            trackSegmentEvent({
                eventName: SegmentEvents.ARTWORK_VIEW_META_DETAILS_CLICKED,
                properties: {
                    email: user?.email,
                    user_id: user?.externalId,
                    metadetails: "Etherscan",
                    artwork_id: tokenData.tokenId,
                    artwork_name: tokenData?.metadata?.name,
                    artist_name:
                        tokenData?.metadata?.properties.artistName?.description,
                },
            }),
        );
        dispatch(
            trackHubSpotEvent({
                eventName: EventNames.artwork_link_clicked,
                email: user?.email,

                properties: {
                    user_id: user?.externalId,

                    action_type: ArtworkLinkClickedEventTypes.view_etherscan,
                    link_name: "Etherscan",
                    link_url: tokenData.tokenURI,
                    page_path: window.location.pathname,
                    page_title: globalThis?.document.title,
                    page_url: window.location.href,
                    ...getUtms(),
                },
            }),
        );
    }, [tokenData, user, dispatch]);

    const downloadFile = useCallback(() => {
        if (tokenData?.metadata) {
            dispatch(
                downloadFileSaga({
                    url: tokenData?.metadata?.image,
                    type: `${tokenData?.metadata?.properties.fileType?.description}`,
                    name:
                        `${tokenData?.metadata?.properties.fileName.description}` ||
                        "",
                    metadata: tokenData?.metadata,
                }),
            );
        }
    }, [tokenData, dispatch]);

    const handleOnMetadataURIClick = useCallback(() => {
        dispatch(
            trackSegmentEvent({
                eventName: SegmentEvents.ARTWORK_VIEW_META_DETAILS_CLICKED,
                properties: {
                    email: user?.email,
                    user_id: user?.externalId,
                    metadetails: "Metadata",
                    artwork_id: tokenData.tokenId,
                    artwork_name: tokenData?.metadata?.name,
                    artist_name:
                        tokenData?.metadata?.properties.artistName?.description,
                },
            }),
        );
        dispatch(
            trackHubSpotEvent({
                eventName: EventNames.artwork_link_clicked,
                email: user?.email,

                properties: {
                    user_id: user?.externalId,

                    action_type: ArtworkLinkClickedEventTypes.view_metadata,
                    link_name: "Metadata",
                    link_url: tokenData.tokenURI,
                    page_path: window.location.pathname,
                    page_title: globalThis?.document.title,
                    page_url: window.location.href,
                    ...getUtms(),
                },
            }),
        );
    }, [tokenData, user, dispatch]);

    const handleOnMediaURIClick = useCallback(
        (e) => {
            e.preventDefault();
            downloadFile();
            dispatch(
                trackSegmentEvent({
                    eventName: SegmentEvents.ARTWORK_VIEW_META_DETAILS_CLICKED,

                    properties: {
                        email: user?.email,
                        user_id: user?.externalId,
                        metadetails: "IPFS",
                        artwork_id: tokenData?.tokenId,
                        artwork_name: tokenData?.metadata?.name,
                        artist_name:
                            tokenData?.metadata?.properties.artistName
                                ?.description,
                    },
                }),
            );
            dispatch(
                trackHubSpotEvent({
                    eventName: EventNames.artwork_link_clicked,
                    email: user?.email,

                    properties: {
                        user_id: user?.externalId,

                        action_type: ArtworkLinkClickedEventTypes.view_ipfs,
                        link_name: "IPFS",
                        link_url: tokenData.tokenURI,
                        page_path: window.location.pathname,
                        page_title: globalThis?.document.title,
                        page_url: window.location.href,
                        ...getUtms(),
                    },
                }),
            );
        },
        [tokenData, user, downloadFile, dispatch],
    );

    const ArtworkInfoView = isDesktop
        ? ArtworkInfoDesktopView
        : ArtworkInfoMobileView;

    return (
        <ArtworkInfoView
            ownedByYou={ownedByYou}
            tokenData={tokenData}
            etherscanUrl={etherscanUrl}
            handleOnChainIdClick={handleOnChainIdClick}
            openTooltip={openTooltip}
            handleTooltipClose={handleTooltipClose}
            handleTooltipOpen={handleTooltipOpen}
            handleOnMetadataURIClick={handleOnMetadataURIClick}
            handleOnMediaURIClick={handleOnMediaURIClick}
            hasActionButton={hasActionButton}
        />
    );
};
